import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Activity, Coins, Cpu, Cloud, HardDrive, History, Mic, Camera } from "lucide-react";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { opsBackfill, opsCosts, opsTrends, saveOpsThreshold } from "../../api/platform";
import { ProviderCostCard } from "./ProviderCostCard";
import { FaceCostTable } from "./FaceCostTable";

const ICONS = {
  gemini: { Icon: Cpu, color: "text-fuchsia-500" },
  openai_chat: { Icon: Cpu, color: "text-emerald-500" },
  openai_tts: { Icon: Mic, color: "text-cyan-500" },
  eleven_tts: { Icon: Mic, color: "text-violet-500" },
  rekognition: { Icon: Camera, color: "text-orange-500" },
  s3: { Icon: HardDrive, color: "text-sky-500" },
};

const money = (v, currency) => `${(v || 0).toFixed(4)} ${String(currency || "usd").toUpperCase()}`;

export const OperationalCostsPanel = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  const [trends, setTrends] = useState({});
  const load = async () => {
    try {
      const [summary, series] = await Promise.all([opsCosts(), opsTrends(12)]);
      setData(summary);
      setTrends(series?.series || {});
    } catch { setData({ providers: [], totals: {}, face_report: null, thresholds: {} }); }
  };
  useEffect(() => { load(); }, []);

  const saveThreshold = async (provider, threshold) => {
    try {
      const res = await saveOpsThreshold(provider, threshold);
      setData((d) => ({ ...d, thresholds: res.thresholds }));
      toast.success(t("opsCosts.thresholdSaved"));
    } catch { toast.error(t("errors.generic")); }
  };

  const runBackfill = async () => {
    try {
      const r = await opsBackfill();
      toast.success(t("opsCosts.backfillDone", { greetings: r.greetings, name_ar: r.name_ar, s3: r.s3 }));
      load();
    } catch { toast.error(t("errors.generic")); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">{t("app.loading")}</p>;
  const face = data.face_report;

  return (
    <div className="grid gap-6" data-testid="ops-costs-panel">
      <NeuCard>
        <div className="flex flex-wrap items-center gap-2">
          <Cloud className="h-4 w-4 text-primary" />
          <p className="label-xs">{t("opsCosts.title")}</p>
          <span className="font-mono text-xs text-muted-foreground" dir="ltr">{data.month}</span>
          <NeuButton data-testid="ops-backfill" onClick={runBackfill}
                     className="ms-auto flex items-center gap-1.5 px-3 py-1.5 text-xs">
            <History className="h-3.5 w-3.5" /> {t("opsCosts.backfill")}
          </NeuButton>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">{t("opsCosts.backfillHint")}</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(data.providers || []).map((p) => (
            <ProviderCostCard key={p.key} providerKey={p.key} calls={p.calls} units={p.units}
                              cost={p.cost} currency={data.currency}
                              threshold={data.thresholds?.[p.key]} onSaveThreshold={saveThreshold}
                              trend={trends[p.key]} {...ICONS[p.key]} />
          ))}
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-background p-4 shadow-neu-sm">
          <div className="flex items-center gap-2">
            <Coins className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-black">{t("opsCosts.totalCost")}</span>
          </div>
          <span className="font-mono text-lg font-black text-destructive">{money(data.totals?.cost, data.currency)}</span>
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-emerald-500" />
            <span className="text-sm font-black">{t("opsCosts.revenue")}</span>
          </div>
          <span className="font-mono text-lg font-black text-emerald-600">{money(data.totals?.revenue, data.currency)}</span>
        </div>
      </NeuCard>
      {face ? (
        <>
          <FaceCostTable title={t("faceCost.byCompany")} rows={face.companies}
                         currency={face.currency} testId="face-cost-companies" />
          <FaceCostTable title={t("faceCost.byEmployee")} rows={face.employees}
                         currency={face.currency} showCompany testId="face-cost-employees" />
        </>
      ) : null}
    </div>
  );
};
